
import React, { useState, useEffect, useMemo } from "react";
import {
	Typography,
	Grid,
	Box,
	Card,
	CardActionArea,
	Avatar,
	Button,
	Stepper,
	Step,
	StepLabel,
	CircularProgress,
	Divider,
	Chip,
	TextField,
	Radio,
	MobileStepper,
	useMediaQuery
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
	LocalHospital as SpecialtyIcon,
	Person as DoctorIcon,
	Event as DateIcon,
	Schedule as TimeIcon,
	CheckCircle as ConfirmIcon,
	ArrowBack as BackIcon,
	Face as PatientIcon
} from "@material-ui/icons";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "react-toastify";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";

registerLocale("es", es);

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
	},
	stepper: {
		padding: theme.spacing(3, 0, 5),
		backgroundColor: "transparent",
	},
	mobileStepper: {
		background: "transparent",
		justifyContent: "center",
		padding: theme.spacing(2),
	},
	stepContent: {
		padding: theme.spacing(2),
	},
	card: {
		height: "100%",
		transition: "transform 0.2s, box-shadow 0.2s",
		border: "1px solid " + theme.palette.divider,
		borderRadius: 12,
		"&:hover": {
			transform: "translateY(-4px)",
			boxShadow: theme.shadows[4],
			borderColor: theme.palette.primary.main,
		},
	},
	cardSelected: {
		borderColor: theme.palette.primary.main,
		backgroundColor: theme.palette.action.selected,
		transform: "scale(1.02)",
		boxShadow: theme.shadows[2],
	},
	iconLarge: {
		fontSize: 48,
		color: theme.palette.primary.main,
		marginBottom: theme.spacing(1),
	},
	avatarLarge: {
		width: 80,
		height: 80,
		margin: "0 auto",
		marginBottom: theme.spacing(2),
		backgroundColor: theme.palette.primary.main,
		fontSize: "2rem",
	},
	timeChip: {
		margin: theme.spacing(0.5),
		fontWeight: 600,
		padding: theme.spacing(1),
		"&.selected": {
			backgroundColor: theme.palette.primary.main,
			color: "#fff",
		},
		"&.disabled": {
			opacity: 0.5,
			backgroundColor: "#e0e0e0",
			color: "#9e9e9e",
			pointerEvents: "none"
		}
	},
	summaryBox: {
		backgroundColor: theme.palette.background.default,
		padding: theme.spacing(3),
		borderRadius: 12,
		border: "1px dashed " + theme.palette.divider,
	},
	actionButtons: {
		marginTop: theme.spacing(4),
		display: "flex",
		justifyContent: "space-between",
	},
	patientOption: {
		padding: theme.spacing(2),
		border: "1px solid " + theme.palette.divider,
		borderRadius: 8,
		marginBottom: theme.spacing(1),
		cursor: "pointer",
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
		"&.selected": {
			borderColor: theme.palette.primary.main,
			backgroundColor: theme.palette.action.selected,
		}
	}
}));

const steps = ["Paciente", "Especialidad", "Profesional", "Fecha y Hora", "Confirmar"];

const ScheduleView = ({ data }) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

	const [activeStep, setActiveStep] = useState(0);
	const [loading, setLoading] = useState(false);
	
	const doctors = data.doctors || [];
	const availablePatients = data.patients || [];
	
	const [specialties, setSpecialties] = useState([]);
	
	const [selectedPatientOption, setSelectedPatientOption] = useState(null);
	const [customPatientName, setCustomPatientName] = useState("");

	const [selectedSpecialty, setSelectedSpecialty] = useState(null);
	const [selectedDoctor, setSelectedDoctor] = useState(null);
	const [selectedDate, setSelectedDate] = useState(new Date());
	const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

	const [doctorSlots, setDoctorSlots] = useState([]);
	const [loadingSlots, setLoadingSlots] = useState(false);

	useEffect(() => {
		if (doctors.length > 0) {
			const specsMap = new Map();
			doctors.forEach(doc => {
				if (doc.professionalTitles) {
					doc.professionalTitles.forEach(title => {
						specsMap.set(title.id, title);
					});
				}
			});
			setSpecialties(Array.from(specsMap.values()));
		}
	}, [doctors]);

	useEffect(() => {
		if (availablePatients.length === 0 && selectedPatientOption !== "other") {
			setSelectedPatientOption("other");
		}
	}, [availablePatients, selectedPatientOption]);

	useEffect(() => {
		if (selectedDoctor && activeStep === 3) {
			const fetchAvailability = async () => {
				setLoadingSlots(true);
				try {
					const { data: res } = await api.get("/public/availability", {
						params: { doctorId: selectedDoctor.id }
					});
					
					setDoctorSlots(res.slots || []);
				} catch (err) {
					console.error(err);
					toastError("Error al cargar horarios");
				} finally {
					setLoadingSlots(false);
				}
			};
			fetchAvailability();
		}
	}, [selectedDoctor, activeStep]);

	const slotsForSelectedDate = useMemo(() => {
		if (!selectedDate || doctorSlots.length === 0) return [];
		const dateStr = format(selectedDate, "yyyy-MM-dd");
		const now = new Date();
		const daySlots = doctorSlots.filter(s => s.date === dateStr);
		return daySlots.map(s => {
			const slotDateTime = new Date(s.date + "T" + s.startTime);
			const isPast = slotDateTime < now;
			return {
				...s,
				label: s.startTime.slice(0, 5),
				disabled: isPast || s.status !== "free"
			};
		}).sort((a, b) => a.startTime.localeCompare(b.startTime));

	}, [selectedDate, doctorSlots]);

	const handleNext = () => setActiveStep((prev) => prev + 1);
	const handleBack = () => setActiveStep((prev) => prev - 1);

	const handleConfirmAppointment = async () => {
		if (!selectedTimeSlot || !selectedDoctor) return;

		const finalPatientName = selectedPatientOption === "other" ? customPatientName : selectedPatientOption;
		if (!finalPatientName || !finalPatientName.trim()) {
			toast.warn("Por favor ingresa el nombre del paciente.");
			return;
		}

		setLoading(true);
		try {
			const isOwner = finalPatientName === data.contactName;
			const patientNamePayload = isOwner ? null : finalPatientName;

			const payload = {
				token: data.token,
				doctorId: selectedDoctor.id,
				professionalTitleId: selectedSpecialty ? selectedSpecialty.id : null,
				date: selectedTimeSlot.date,
				startTime: selectedTimeSlot.startTime,
				endTime: selectedTimeSlot.endTime,
				patientName: patientNamePayload,
				notes: "Agendado desde Web"
			};

			await api.post("/public/appointments", payload);

			toast.success("¡Tu cita ha sido agendada con éxito!");
			setActiveStep(5);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const renderPatientStep = () => {
		if (availablePatients.length === 0) {
			return (
				<div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
					<Typography variant="h6" gutterBottom>
						¿Cuál es tu nombre completo?
					</Typography>
					<Box mt={3}>
						<TextField 
							fullWidth 
							label="Nombre y Apellido" 
							variant="outlined" 
							value={customPatientName}
							onChange={(e) => setCustomPatientName(e.target.value)}
							autoFocus
							helperText="Usaremos este nombre para reservar tu cita."
						/>
					</Box>
				</div>
			);
		}

		return (
			<div style={{ maxWidth: 600, margin: "0 auto" }}>
				<Typography variant="h6" align="center" gutterBottom>
					¿Para quién es la cita?
				</Typography>
				
				<Box mt={3}>
					{availablePatients.map((name, idx) => (
						<div 
							key={idx}
							className={classes.patientOption + (selectedPatientOption === name ? " selected" : "")}
							onClick={() => setSelectedPatientOption(name)}
						>
							<Box display="flex" alignItems="center">
								<Radio 
									checked={selectedPatientOption === name} 
									color="primary"
									onChange={() => setSelectedPatientOption(name)}
								/>
								<Typography variant="body1" style={{ fontWeight: 500 }}>
									{name}
								</Typography>
							</Box>
						</div>
					))}

					<div 
						className={classes.patientOption + (selectedPatientOption === "other" ? " selected" : "")}
						onClick={() => setSelectedPatientOption("other")}
					>
						<Box display="flex" alignItems="center">
							<Radio 
								checked={selectedPatientOption === "other"} 
								color="primary"
								onChange={() => setSelectedPatientOption("other")}
							/>
							<Typography variant="body1">
								Otra persona / Nuevo paciente
							</Typography>
						</Box>
						
						{selectedPatientOption === "other" && (
							<Box ml={4} mt={1} mr={2}>
								<TextField 
									fullWidth 
									label="Nombre completo del paciente" 
									variant="outlined" 
									size="small"
									value={customPatientName}
									onChange={(e) => setCustomPatientName(e.target.value)}
									autoFocus
								/>
							</Box>
						)}
					</div>
				</Box>
			</div>
		);
	};

	const renderSpecialtyStep = () => (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Typography variant="h6" align="center" gutterBottom>
					¿Qué especialidad necesitas?
				</Typography>
			</Grid>
			{specialties.map(spec => (
				<Grid item xs={12} sm={6} md={4} key={spec.id}>
					<Card 
						className={classes.card + (selectedSpecialty?.id === spec.id ? " " + classes.cardSelected : "")} 
						variant="outlined"
					>
						<CardActionArea 
							onClick={() => { setSelectedSpecialty(spec); handleNext(); }}
							style={{ height: "100%", padding: 20, textAlign: "center" }}
						>
							<SpecialtyIcon className={classes.iconLarge} />
							<Typography variant="h6">{spec.title}</Typography>
						</CardActionArea>
					</Card>
				</Grid>
			))}
		</Grid>
	);

	const renderDoctorStep = () => {
		const filteredDoctors = doctors.filter(doc => 
			doc.professionalTitles && doc.professionalTitles.some(t => t.id === selectedSpecialty.id)
		);

		return (
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography variant="h6" align="center" gutterBottom>
						Elige un profesional de {selectedSpecialty.title}
					</Typography>
				</Grid>
				{filteredDoctors.map(doc => (
					<Grid item xs={12} sm={6} md={4} key={doc.id}>
						<Card 
							className={classes.card + (selectedDoctor?.id === doc.id ? " " + classes.cardSelected : "")} 
							variant="outlined"
						>
							<CardActionArea 
								onClick={() => { setSelectedDoctor(doc); handleNext(); }}
								style={{ height: "100%", padding: 20, textAlign: "center" }}
							>
								<Avatar className={classes.avatarLarge} src={doc.profilePicUrl} />
								<Typography variant="h6">{doc.name}</Typography>
								<Typography variant="body2" color="textSecondary">{selectedSpecialty.title}</Typography>
							</CardActionArea>
						</Card>
					</Grid>
				))}
			</Grid>
		);
	};

	const renderDateStep = () => (
		<Grid container spacing={4} justifyContent="center">
			<Grid item xs={12} md={5}>
				<Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold" }}>
					1. Selecciona el día
				</Typography>
				<div style={{ display: "flex", justifyContent: "center" }}>
					<DatePicker
						selected={selectedDate}
						onChange={(date) => { setSelectedDate(date); setSelectedTimeSlot(null); }}
						inline
						minDate={new Date()}
						locale="es"
					/>
				</div>
			</Grid>
			
			<Grid item xs={12} md={7}>
				<Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold" }}>
					2. Selecciona el horario
				</Typography>
				
				{loadingSlots ? (
					<Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
				) : slotsForSelectedDate.length > 0 ? (
					<Box display="flex" flexWrap="wrap" justifyContent="flex-start">
						{slotsForSelectedDate.map((slot, index) => (
							<Chip
								key={index}
								label={slot.label}
								onClick={() => !slot.disabled && setSelectedTimeSlot(slot)}
								clickable={!slot.disabled}
								color={selectedTimeSlot?.label === slot.label ? "primary" : "default"}
								className={classes.timeChip + (selectedTimeSlot?.label === slot.label ? " selected" : "") + (slot.disabled ? " disabled" : "")}
								variant={selectedTimeSlot?.label === slot.label ? "default" : "outlined"}
								style={{ cursor: slot.disabled ? "not-allowed" : "pointer" }}
							/>
						))}
					</Box>
				) : (
					<Box p={2} bgcolor="#fff3e0" borderRadius={4} textAlign="center">
						<Typography color="error">No hay horarios disponibles para este día.</Typography>
						<Typography variant="caption">Intenta seleccionar otra fecha en el calendario.</Typography>
					</Box>
				)}
			</Grid>
		</Grid>
	);

	const renderConfirmStep = () => {
		const finalPatientName = selectedPatientOption === "other" ? customPatientName : selectedPatientOption;

		return (
			<div style={{ maxWidth: 600, margin: "0 auto" }}>
				<Typography variant="h5" align="center" gutterBottom>
					Resumen de tu Cita
				</Typography>
				
				<Box className={classes.summaryBox}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Box display="flex" alignItems="center">
								<PatientIcon color="primary" style={{ marginRight: 10 }} />
								<div>
									<Typography variant="caption" color="textSecondary">Paciente</Typography>
									<Typography variant="body1" style={{ fontWeight: 600 }}>{finalPatientName}</Typography>
								</div>
							</Box>
						</Grid>
						<Divider style={{ width: "100%", margin: "10px 0" }} />
						<Grid item xs={12}>
							<Box display="flex" alignItems="center">
								<SpecialtyIcon color="primary" style={{ marginRight: 10 }} />
								<div>
									<Typography variant="caption" color="textSecondary">Especialidad</Typography>
									<Typography variant="body1" style={{ fontWeight: 600 }}>{selectedSpecialty?.title}</Typography>
								</div>
							</Box>
						</Grid>
						<Divider style={{ width: "100%", margin: "10px 0" }} />
						<Grid item xs={12}>
							<Box display="flex" alignItems="center">
								<DoctorIcon color="primary" style={{ marginRight: 10 }} />
								<div>
									<Typography variant="caption" color="textSecondary">Profesional</Typography>
									<Typography variant="body1" style={{ fontWeight: 600 }}>{selectedDoctor?.name}</Typography>
								</div>
							</Box>
						</Grid>
						<Divider style={{ width: "100%", margin: "10px 0" }} />
						<Grid item xs={12}>
							<Box display="flex" alignItems="center">
								<DateIcon color="primary" style={{ marginRight: 10 }} />
								<div>
									<Typography variant="caption" color="textSecondary">Fecha</Typography>
									<Typography variant="body1" style={{ fontWeight: 600 }}>
										{selectedDate ? format(selectedDate, "EEEE d 'de' MMMM 'de' yyyy", { locale: es }) : ""}
									</Typography>
								</div>
							</Box>
						</Grid>
						<Divider style={{ width: "100%", margin: "10px 0" }} />
						<Grid item xs={12}>
							<Box display="flex" alignItems="center">
								<TimeIcon color="primary" style={{ marginRight: 10 }} />
								<div>
									<Typography variant="caption" color="textSecondary">Hora</Typography>
									<Typography variant="body1" style={{ fontWeight: 600 }}>
										{selectedTimeSlot?.label} hs
									</Typography>
								</div>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</div>
		);
	};

	if (activeStep === 5) {
		return (
			<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={400}>
				<ConfirmIcon style={{ fontSize: 80, color: "#4caf50", marginBottom: 20 }} />
				<Typography variant="h4" gutterBottom style={{ fontWeight: "bold" }}>¡Cita Agendada!</Typography>
				<Typography align="center" color="textSecondary">
					Te esperamos el {format(selectedDate, "dd/MM", { locale: es })} a las {selectedTimeSlot?.label}.
				</Typography>
				<Button 
					variant="outlined" 
					color="primary" 
					style={{ marginTop: 30 }}
					onClick={() => window.location.reload()} 
				>
					Agendar Otra
				</Button>
			</Box>
		);
	}

	return (
		<div className={classes.root}>
			{isMobile ? (
				<MobileStepper
					variant="dots"
					steps={5}
					position="static"
					activeStep={activeStep}
					className={classes.mobileStepper}
					backButton={null} 
					nextButton={null}
				/>
			) : (
				<Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
					{steps.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
			)}

			<div className={classes.stepContent}>
				{activeStep === 0 && renderPatientStep()}
				{activeStep === 1 && renderSpecialtyStep()}
				{activeStep === 2 && renderDoctorStep()}
				{activeStep === 3 && renderDateStep()}
				{activeStep === 4 && renderConfirmStep()}

				<div className={classes.actionButtons}>
					<Button
						disabled={activeStep === 0 || loading}
						onClick={handleBack}
						startIcon={<BackIcon />}
					>
						Atrás
					</Button>
					
					{(activeStep !== 5) && (
						<Button
							variant="contained"
							color="primary"
							onClick={activeStep === 4 ? handleConfirmAppointment : handleNext}
							disabled={
								(activeStep === 0 && (!selectedPatientOption || (selectedPatientOption === "other" && !customPatientName.trim()))) ||
								(activeStep === 3 && !selectedTimeSlot) || 
								loading
							}
						>
							{activeStep === 4 ? (loading ? "Confirmando..." : "Confirmar Cita") : "Siguiente"}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

export default ScheduleView;